import { Download, Eye, LogOut, MailCheck, RefreshCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import {
  clearAdminToken,
  exportLeadsCsv,
  fetchAdminSession,
  fetchLeads,
  getAdminToken,
  updateLeadStatus,
} from "@/lib/api";

const formatDate = (value) => {
  const date = new Date(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusBadgeClass = {
  new: "border border-emerald-200 bg-emerald-50 text-emerald-700",
  contacted: "border border-brand-navy/20 bg-brand-mist text-brand-navy",
};

export const AdminLeadsPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [adminUsername, setAdminUsername] = useState("");
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filters, setFilters] = useState({ enquiryType: "all", status: "all" });
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const loadLeads = async (activeToken, activeFilters) => {
    setIsLoading(true);
    try {
      const data = await fetchLeads(activeToken, activeFilters);
      setLeads(data);
    } catch (error) {
      if (error?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin", { replace: true });
        return;
      }
      toast.error("Unable to load leads", {
        description: error?.response?.data?.detail || "Please refresh and try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const existingToken = getAdminToken();
    if (!existingToken) {
      navigate("/admin", { replace: true });
      return;
    }

    setToken(existingToken);
    fetchAdminSession(existingToken)
      .then((session) => {
        setAdminUsername(session.username);
        return loadLeads(existingToken, filters);
      })
      .catch(() => {
        clearAdminToken();
        navigate("/admin", { replace: true });
      });
  }, [navigate]);

  useEffect(() => {
    let robotsMeta = document.querySelector('meta[name="robots"]');
    const previousContent = robotsMeta?.getAttribute("content") || "";

    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }

    robotsMeta.setAttribute("content", "noindex, nofollow");

    return () => {
      if (previousContent) {
        robotsMeta.setAttribute("content", previousContent);
      } else {
        robotsMeta.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (token) {
      loadLeads(token, filters);
    }
  }, [filters, token]);

  const summary = useMemo(() => ({
    total: leads.length,
    newCount: leads.filter((lead) => lead.status === "new").length,
    contactedCount: leads.filter((lead) => lead.status === "contacted").length,
  }), [leads]);

  const handleStatusChange = async (leadId, status) => {
    try {
      const updatedLead = await updateLeadStatus(token, leadId, status);
      setLeads((current) => current.map((lead) => (lead.id === leadId ? updatedLead : lead)));
      setSelectedLead((current) => (current?.id === leadId ? updatedLead : current));
      toast.success(`Lead marked as ${status}`);
    } catch (error) {
      toast.error("Unable to update lead", {
        description: error?.response?.data?.detail || "Please try again.",
      });
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportLeadsCsv(token, filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "carefleet-leads.csv";
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV export downloaded");
    } catch (error) {
      toast.error("Unable to export leads", {
        description: error?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="page-shell min-h-screen px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <p className="eyebrow">Internal admin area</p>
            <h1 className="font-heading text-4xl font-semibold text-slate-950" data-testid="admin-leads-title">
              Website lead dashboard
            </h1>
            <p className="text-base leading-8 text-slate-600" data-testid="admin-leads-description">
              View enquiries, update follow-up status, and export leads for the CareFleet team.
            </p>
            <p className="text-sm text-slate-500" data-testid="admin-leads-username">
              Signed in as {adminUsername || "carefleet-admin"}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button onClick={handleExport} disabled={isExporting} className="rounded-full bg-brand-navy px-5 text-white hover:bg-brand-navy/90" data-testid="admin-export-csv-button">
              <Download className="h-4 w-4" />
              {isExporting ? "Exporting" : "Export CSV"}
            </Button>
            <Button variant="outline" onClick={() => loadLeads(token, filters)} className="rounded-full border-brand-navy/20 px-5 text-brand-navy" data-testid="admin-refresh-button">
              <RefreshCcw className="h-4 w-4" />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleLogout} className="rounded-full border-slate-300 px-5" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {[
            { label: "Total leads", value: summary.total },
            { label: "New", value: summary.newCount },
            { label: "Contacted", value: summary.contactedCount },
          ].map((card) => (
            <div key={card.label} className="soft-card p-6" data-testid={`admin-summary-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <p className="text-sm font-medium text-slate-500">{card.label}</p>
              <p className="mt-3 font-heading text-4xl font-semibold text-slate-950">{card.value}</p>
            </div>
          ))}
        </div>

        <div className="soft-card p-6" data-testid="admin-filter-card">
          <div className="grid gap-4 md:grid-cols-[220px_220px_auto] md:items-end">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Enquiry type</label>
              <Select value={filters.enquiryType} onValueChange={(value) => setFilters((prev) => ({ ...prev, enquiryType: value }))}>
                <SelectTrigger className="h-12 rounded-2xl bg-white" data-testid="admin-enquiry-filter">
                  <SelectValue placeholder="All enquiry types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All enquiry types</SelectItem>
                  <SelectItem value="demo">Demo</SelectItem>
                  <SelectItem value="contact">Contact</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Status</label>
              <Select value={filters.status} onValueChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}>
                <SelectTrigger className="h-12 rounded-2xl bg-white" data-testid="admin-status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="soft-card p-6" data-testid="admin-leads-table-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Organisation</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Enquiry type</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-10 text-center text-slate-500" data-testid="admin-loading-row">
                    Loading website leads…
                  </TableCell>
                </TableRow>
              ) : leads.length ? (
                leads.map((lead) => (
                  <TableRow key={lead.id} data-testid={`admin-lead-row-${lead.id}`}>
                    <TableCell className="font-medium text-slate-900">{lead.full_name}</TableCell>
                    <TableCell>{lead.organisation || "—"}</TableCell>
                    <TableCell>{lead.email}</TableCell>
                    <TableCell>{lead.phone}</TableCell>
                    <TableCell className="capitalize">{lead.enquiry_type}</TableCell>
                    <TableCell>{formatDate(lead.submitted_at)}</TableCell>
                    <TableCell>
                      <Badge className={statusBadgeClass[lead.status]} data-testid={`admin-status-${lead.id}`}>
                        {lead.status === "new" ? "New" : "Contacted"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button variant="outline" className="rounded-full" onClick={() => setSelectedLead(lead)} data-testid={`admin-view-message-${lead.id}`}>
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                        <Button variant="outline" className="rounded-full" onClick={() => handleStatusChange(lead.id, lead.status === "new" ? "contacted" : "new")} data-testid={`admin-toggle-status-${lead.id}`}>
                          <MailCheck className="h-4 w-4" />
                          {lead.status === "new" ? "Mark Contacted" : "Mark New"}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="py-10 text-center text-slate-500" data-testid="admin-empty-row">
                    No leads match the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={Boolean(selectedLead)} onOpenChange={(open) => (!open ? setSelectedLead(null) : null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="admin-message-dialog">
          <DialogHeader>
            <DialogTitle data-testid="admin-message-dialog-title">Lead message detail</DialogTitle>
            <DialogDescription data-testid="admin-message-dialog-description">
              View the full enquiry and update its follow-up status.
            </DialogDescription>
          </DialogHeader>

          {selectedLead ? (
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Name</p>
                  <p className="mt-2 text-base text-slate-900" data-testid="admin-message-full-name">{selectedLead.full_name}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Organisation</p>
                  <p className="mt-2 text-base text-slate-900" data-testid="admin-message-organisation">{selectedLead.organisation || "—"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Email</p>
                  <p className="mt-2 text-base text-slate-900" data-testid="admin-message-email">{selectedLead.email}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Phone</p>
                  <p className="mt-2 text-base text-slate-900" data-testid="admin-message-phone">{selectedLead.phone}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Message</p>
                <div className="mt-3 rounded-3xl border border-slate-200 bg-slate-50 p-5 text-base leading-7 text-slate-700" data-testid="admin-message-body">
                  {selectedLead.message}
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button onClick={() => handleStatusChange(selectedLead.id, "contacted")} className="rounded-full bg-brand-navy text-white hover:bg-brand-navy/90" data-testid="admin-dialog-mark-contacted-button">
                  Mark as Contacted
                </Button>
                <Button variant="outline" onClick={() => handleStatusChange(selectedLead.id, "new")} className="rounded-full border-brand-navy/20 text-brand-navy" data-testid="admin-dialog-mark-new-button">
                  Mark as New
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};